import { useEffect, useRef, useState } from "react";
import { Flag } from "lucide-react";

const avg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);

export const ObstaclePathGame = ({ difficulty, onDone }) => {
  const { obstacles, window_ms, labels } = difficulty;
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [last, setLast] = useState(null);
  const startRef = useRef(0);
  const scoresRef = useRef([]);
  const doneRef = useRef(false);
  const raf = useRef();

  useEffect(() => {
    startRef.current = performance.now();
    doneRef.current = false;
    const tick = (t) => {
      const p = (t - startRef.current) / window_ms;
      if (p >= 1) { record(0.12, "Spooked!"); return; }
      setProgress(p);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const record = (s, label) => {
    if (doneRef.current) return;
    doneRef.current = true;
    cancelAnimationFrame(raf.current);
    const next = [...scoresRef.current, s];
    scoresRef.current = next;
    setLast(label);
    setProgress(0);
    if (next.length >= obstacles) {
      setTimeout(() => onDone(avg(next)), 400);
      setIdx(obstacles);
    } else {
      setTimeout(() => setIdx(next.length), 250);
    }
  };

  const tap = () => {
    const p = (performance.now() - startRef.current) / window_ms;
    let s, label;
    if (p >= 0.6 && p <= 0.9) { s = 1 - (Math.abs(p - 0.75) / 0.15) * 0.3; label = "Clean!"; }
    else if (p < 0.6) { s = Math.max(0.25, 0.55 - (0.6 - p)); label = "Too early"; }
    else { s = 0.2; label = "Too late"; }
    record(s, label);
  };

  const done = idx >= obstacles;

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="minigame-trail">
      <div className="flex items-center justify-between text-xs font-bold text-primary">
        <span className="flex items-center gap-1"><Flag size={14} /> {done ? "Trail complete!" : labels[idx]}</span>
        <span data-testid="trail-progress">Obstacle {Math.min(idx + 1, obstacles)} / {obstacles}</span>
      </div>

      <div className="relative mt-4 h-9 overflow-hidden rounded-full bg-muted">
        <div className="absolute top-0 h-full bg-green-400/60" style={{ left: "60%", width: "30%" }} />
        <div className="absolute top-0 h-full bg-secondary/70 transition-none"
          style={{ width: `${progress * 100}%` }} data-testid="trail-fill" />
      </div>

      {last && (
        <p className="mt-2 text-center text-xs font-bold" data-testid="trail-last"
          style={{ color: last === "Clean!" ? "#15803d" : last === "Spooked!" ? "#b91c1c" : "#b45309" }}>{last}</p>
      )}

      <button data-testid="trail-tap-btn" disabled={done} onClick={tap}
        className="mt-3 h-14 w-full rounded-xl bg-secondary text-lg font-bold text-white disabled:opacity-40 active:scale-95">
        GO
      </button>
    </div>
  );
};
